/**
 * MessageActionsMenu — 3-dot dropdown on user messages
 *
 * Offers actions like Restore versions, Summarize, Branch. ("Revert AI changes"
 * — a reverse patch from inline file text — was removed in #768; restoring goes
 * through the version store.)
 * Appears on hover over user message bubbles.
 *
 * Uses fixed positioning + portal to avoid clipping by overflow containers.
 */
import { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { MoreVertical, GitBranch, Sparkles, History } from 'lucide-react'
import { useTheme, getThemeClass, useAnchoredPosition } from '@papan/core-ui/ui'

export type MessageAction =
  | 'restore-versions'
  | 'restore-versions-after'
  | 'summarize'
  | 'branch'

interface Props {
  messageId: string
  onAction: (action: MessageAction, messageId: string) => void
  hasChanges?: boolean
}

const MENU_ITEMS: Array<{
  action: MessageAction
  label: string
  icon: React.ReactNode
  needsChanges?: boolean
}> = [
  {
    action: 'restore-versions',
    label: 'Restore version prior to this',
    icon: <History size={12} />,
    needsChanges: true,
  },
  {
    action: 'restore-versions-after',
    label: 'Restore version of this',
    icon: <History size={12} />,
    needsChanges: true,
  },
  {
    action: 'branch',
    label: 'Branch from here',
    icon: <GitBranch size={12} />,
  },
  {
    action: 'summarize',
    label: 'Summarize session',
    icon: <Sparkles size={12} />,
  },
]

export function MessageActionsMenu({ messageId, onAction, hasChanges = false }: Props) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const [open, setOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const handleToggle = useCallback(() => setOpen(o => !o), [])

  // Placement, follow-on-scroll and anchor-exit — the shared convention
  // (`useAnchoredPosition`). Replaces a hand-rolled placement that guessed the
  // menu's height at a hardcoded 200px and its width at another 200px, so the
  // flip fired against a number that had nothing to do with the rendered menu.
  // The hook measures the real element. The old "close on any scroll" is gone
  // too: the message list scrolls constantly, and closing while the button is
  // still on screen was the more annoying half of that trade.
  const { pos, exiting, origin } = useAnchoredPosition({
    open,
    anchorRef: buttonRef,
    floatingRef: menuRef,
    onDismiss: useCallback(() => setOpen(false), []),
    align: 'end',
    exitTransitionMs: 160,
  })

  // Close on click outside
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target as Node) &&
        buttonRef.current && !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const visibleItems = MENU_ITEMS.filter(item => {
    if (item.needsChanges && !hasChanges) return false
    return true
  })

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className="p-0.5 rounded themed-text-muted hover:themed-text hover:themed-bg-tertiary transition-colors"
        title="Message actions"
      >
        <MoreVertical size={14} />
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          className={`${themeClass} fixed z-[9999] min-w-[112px] themed-bg-secondary themed-border border rounded-lg shadow-xl py-1${exiting ? ' anchor-exit' : ''}`}
          // Rendered before measured — the hook needs this element to exist.
          style={pos
            ? { top: pos.top, left: pos.left, ...(origin ? { transformOrigin: `${origin.x}px ${origin.y}px` } : {}) }
            : { top: 0, left: 0, visibility: 'hidden' }}
        >
          {visibleItems.map(item => (
            <button
              key={item.action}
              onClick={() => {
                setOpen(false)
                onAction(item.action, messageId)
              }}
              className="w-full flex items-center gap-1.5 px-3 py-1.5 text-xs themed-text hover:themed-bg-tertiary transition-colors text-left"
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </>
  )
}
